// foodGuidance.ts — pure data types + helpers for medication food/drink guidance.
// Safe to import from both the API (Lambda) and the web SPA.
// NOT medical advice — advisory personal-health log only (PLAN.md D-1).
// See MEDICATION_FOOD_GUIDANCE_PLAN.md for the full feature spec.

import type { ScheduledGroup } from "./medication.js";

// ---------------------------------------------------------------------------
// Core types
// ---------------------------------------------------------------------------

export const FOOD_TIMINGS = ["with_food", "empty_stomach", "either", "unknown"] as const;
export type FoodTiming = (typeof FOOD_TIMINGS)[number];

export const FOOD_AVOID_CATEGORIES = [
  "grapefruit",
  "alcohol",
  "dairy_calcium",
  "caffeine",
  "tyramine",
  "antacids",
  "other",
] as const;
export type FoodAvoidCategory = (typeof FOOD_AVOID_CATEGORIES)[number];

export interface FoodAvoidItem {
  category: FoodAvoidCategory;
  /** Human display label, e.g. "grapefruit / grapefruit juice". */
  label: string;
  /** <= 1 short sentence explaining the interaction. */
  reason: string;
}

export type FoodGuidanceSource = "curated" | "openfda" | "llm" | "user";
export type FoodGuidanceConfidence = "high" | "medium" | "low";

export interface FoodGuidance {
  /** Normalized lowercase name used as the global cache key / canonical identity. */
  ingredient: string;
  displayName?: string;
  rxcui?: string;

  timing: FoodTiming;
  /** <= 1 short sentence explaining why the timing matters. */
  timingReason?: string;
  /** Food/drink items to avoid — may be empty. */
  avoid: FoodAvoidItem[];
  /** Short spacing/administration notes — may be empty. */
  spacing: string[];

  source: FoodGuidanceSource;
  confidence: FoodGuidanceConfidence;
  /**
   * True only when a patient's own prescriber instructions overrode the derived
   * timing (FG-4). NEVER set on a cached global record — applied per-request,
   * in memory only.
   */
  userOverride?: boolean;
  notes?: string;
  /** ISO timestamp the record was resolved. */
  retrievedAt: string;
}

// ---------------------------------------------------------------------------
// Capture-note item (shared between API and web so types agree; C1/D1 in plan)
// ---------------------------------------------------------------------------

export interface FoodGuidanceNoteItem {
  /** Patient's recorded med name. */
  name: string;
  timing: FoodTiming;
  timingReason?: string;
  avoid: FoodAvoidItem[];
  spacing: string[];
  source: FoodGuidanceSource;
  confidence: FoodGuidanceConfidence;
  userOverride?: boolean;
  /** Set when the dose's scheduledGroup conflicts with the timing (FG-3). */
  mismatchMessage?: string;
}

// ---------------------------------------------------------------------------
// Display labels
// ---------------------------------------------------------------------------

export const FOOD_TIMING_LABEL: Record<FoodTiming, string> = {
  with_food: "Take with food",
  empty_stomach: "Take on empty stomach",
  either: "With or without food",
  unknown: "No specific food timing",
};

export const FOOD_GUIDANCE_SOURCE_LABEL: Record<FoodGuidanceSource, string> = {
  curated: "curated reference",
  openfda: "FDA drug label",
  llm: "AI estimate",
  user: "your instructions",
};

// ---------------------------------------------------------------------------
// Meal-anchor helpers
// ---------------------------------------------------------------------------

export const MEAL_ANCHOR_GROUPS: ReadonlySet<ScheduledGroup> = new Set([
  "breakfast",
  "lunch",
  "dinner",
]);

export function isMealAnchor(group: ScheduledGroup): boolean {
  return MEAL_ANCHOR_GROUPS.has(group);
}

// ---------------------------------------------------------------------------
// Badge / summary helpers
// ---------------------------------------------------------------------------

/**
 * True when the timing value alone carries an actionable instruction.
 * `with_food` / `empty_stomach` tell the user to do something; `either`
 * ("any time") and `unknown` do not, so they are not worth a card badge or a
 * standalone timing row (FG-5). The nuance for an `either` med, when it exists,
 * lives in `timingReason` and is surfaced separately in the info modal.
 */
export function isInformativeTiming(timing: FoodTiming): boolean {
  return timing === "with_food" || timing === "empty_stomach";
}

/**
 * Returns a short display string for the food-guidance badge.
 * Returns "" when there is nothing worth showing: no `avoid` warnings and a
 * non-informative timing (`either`/`unknown`). Avoid warnings always win.
 */
export function formatFoodGuidanceShort(g: FoodGuidance): string {
  if (!hasFoodGuidance(g)) return "";
  if (g.avoid.length > 0) {
    const MAX_SHOWN = 2;
    const shown = g.avoid.slice(0, MAX_SHOWN).map((a) => a.label);
    const extra = g.avoid.length - MAX_SHOWN;
    const list = extra > 0 ? [...shown, `+${extra} more`].join(", ") : shown.join(", ");
    return `Avoid ${list}`;
  }
  if (!isInformativeTiming(g.timing)) return "";
  return FOOD_TIMING_LABEL[g.timing];
}

/**
 * True when there is actionable food guidance to show.
 * A `timing:"unknown"` with empty avoid/spacing is a resolved negative — no badge.
 */
export function hasFoodGuidance(g: FoodGuidance): boolean {
  return g.timing !== "unknown" || g.avoid.length > 0 || g.spacing.length > 0;
}

// ---------------------------------------------------------------------------
// User-override parser
// ---------------------------------------------------------------------------

const EMPTY_STOMACH_RE =
  /empty\s+stomach|before\s+(?:a\s+)?meal|on\s+an\s+empty|without\s+food|1\s+hour\s+before|2\s+hours?\s+after\s+eating/i;

const WITH_FOOD_RE =
  /with\s+(?:food|a\s+meal|meals?|milk)|after\s+(?:a\s+)?meal|after\s+eating|take.*?food/i;

/**
 * Parses free-text prescriber instructions for a food-timing hint.
 * Returns `null` when nothing is recognized.
 * `empty_stomach` patterns are tested first (more specific).
 */
export function parseUserFoodTiming(instructions?: string): FoodTiming | null {
  if (!instructions) return null;
  if (EMPTY_STOMACH_RE.test(instructions)) return "empty_stomach";
  if (WITH_FOOD_RE.test(instructions)) return "with_food";
  return null;
}

// ---------------------------------------------------------------------------
// Log-time timing mismatch (FG-3)
// ---------------------------------------------------------------------------

/**
 * Checks whether a dose's scheduled group conflicts with the drug's food timing.
 * Returns `{ mismatch: false }` when there is no issue or timing is unknown/either.
 * Copy is informational and ends with "consider" — never imperative (D-1).
 */
export function evaluateFoodTimingMismatch(
  timing: FoodTiming,
  group: ScheduledGroup,
  displayName: string
): { mismatch: boolean; message?: string } {
  if (timing === "unknown" || timing === "either") return { mismatch: false };

  const atMeal = isMealAnchor(group);
  const groupLabel = group.charAt(0).toUpperCase() + group.slice(1);

  if (timing === "with_food" && !atMeal) {
    return {
      mismatch: true,
      message: `${displayName} is often taken with food, but this dose is logged at ${groupLabel} — consider taking it with a meal or snack.`,
    };
  }

  if (timing === "empty_stomach" && atMeal) {
    return {
      mismatch: true,
      message: `${displayName} is often taken on an empty stomach, but this dose is logged at ${groupLabel}.`,
    };
  }

  return { mismatch: false };
}
